import isEqual from 'lodash/isEqual';
import {produce} from 'immer';
import {jsx} from 'panel';

import {SelectEl} from 'common/components/select';
import {ChartType, ReportName} from 'common/report/constants';
import {ReportVisualization} from 'common/report/report-manager/visualization';
import {ChartResultChartTypes} from 'common/report/insights/util/constants';
import {getSelectedSegments} from 'common/report/insights/util/segments';
import {getDateRange} from 'common/report/insights/util/time';
import {
  AnalysisType,
  DisplayOptions,
  InsightsBookmarkParams,
  InsightsQueryError,
  InsightsQueryResults,
} from 'common/report/insights/util/types';
import {isLiftEnabled} from 'common/report/util/lift';
import {getOwnAttr} from 'common/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ChartHeaderUpdateDetail} from 'common/widgets/chart-header';
import {OrderedStandardTimeUnits, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {ChartHeaderAttrs} from 'common/widgets/chart-header/types';
import {propagateEvent} from 'common/util/dom';
import {SegmentFilterDataTableJsxProps} from 'common/charts/misc/segment-filter-data-table';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {ColumnResizeEvent, SortChangeEvent} from 'common/components/data-table/types';
import {DataSortMenuEl} from 'common/widgets/data-sort-menu';
import {ReportStatus} from 'common/widgets/warning-hero/constants';
import {canUseViewN, isPivotTableEnabled} from 'common/components/data-table/utils';
import {SortBy} from 'common/report/util/sorting/types';
import {VIEW_N_DEFAULT_LIMIT} from 'common/components/data-table/constants';

import {Report} from '../models/report';
import {InsightsChartDisplayEl} from './charts/chart-display';
import {
  bookmarkUpdatesFromAnalysisChange,
  bookmarkUpdatesFromHeaderUpdate,
  correctDisplayOptions,
  getAnalysisMenuItems,
  getLeafLevelSegments,
  getRollingOptions,
  getSelectedAnalysis,
  getSelectedAnalysisIcon,
  getSelectedChartType,
  getSelectedRollingOption,
  getTimeComparisonDisabledReason,
  getTimeComparisonDisabledReasonMessage,
  getValueRepresentations,
  shouldRequeryDisplayOptions,
} from './util';
import {getDataTableTrackName} from './charts/chart-display/utils';

export class InsightsVisualization extends ReportVisualization<
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsQueryError
> {
  reportName = ReportName.Insights;
  get defaultState() {
    return {
      // TODO remove this once table column widths are persisted to bookmark
      tableChartColumnWidthOverrides: {},
    };
  }

  dataTableChartEnabled() {
    return isPivotTableEnabled(this.project);
  }

  chartDisplay() {
    return (
      <InsightsChartDisplayEl
        attrs={{
          'resizing-layout': this.attr(`show-resizing-layout`),
          elref: `chartDisplay`,
          'enable-pivot-view': this.dataTableChartEnabled(),
          'can-use-view-n': canUseViewN(this.project),
        }}
        props={{
          bookmarkParams: this.bookmarkParams,
          queryResults: this.results,
          queryError: null,
          dataGroups: this.dataGroups,
          tableChartColumnWidthOverrides: this.state.tableChartColumnWidthOverrides,
        }}
        on={{
          'export-csv': (ev) => propagateEvent(this, ev),
          update: ({detail}) => {
            let requery = true;

            // chart updates will not have changes alongside sorting or columnWidths,
            // so safe to assume we don't have to query when present.
            const {sorting, columnWidths} = detail.delta;
            if (sorting || columnWidths) {
              requery = false;
            }

            this.dispatchUpdate({
              params: {...this.bookmarkParams, ...detail.delta},
              requery,
            });
          },
          columnResize: (ev) => {
            this.handleDataTableColumnResize(ev);
          },
        }}
      />
    );
  }

  reportSpecificDataTableAttrs(): SegmentFilterDataTableJsxProps['attrs'] {
    const trackName = getDataTableTrackName(this.bookmarkParams.displayOptions.chartType);
    return {'track-name': trackName};
  }

  reportSpecificResizingLayoutAttrs() {
    if (this.bookmarkParams?.displayOptions?.chartType === ChartType.Table) {
      return {'disable-content-padding': true};
    }
    return {};
  }

  getBookmarkParamsFromSortChange(ev: SortChangeEvent): InsightsBookmarkParams {
    const {chartType} = this.bookmarkParams.displayOptions;
    let sortConfig = ev.detail.sorting;
    if (
      canUseViewN(this.project) &&
      chartType === ChartType.Table &&
      this.bookmarkParams.sorting[chartType].sortBy !== sortConfig.sortBy
    ) {
      // Use default viewNLimits when SortBy changes
      sortConfig = produce(sortConfig, (draft) => {
        if (draft.sortBy === SortBy.Value) {
          draft.viewNLimit = VIEW_N_DEFAULT_LIMIT;
        } else {
          draft.colSortAttrs.forEach((colSortAttr) => {
            colSortAttr.viewNLimit = VIEW_N_DEFAULT_LIMIT;
          });
        }
      });
    }

    return {
      ...this.bookmarkParams,
      sorting: {
        ...this.bookmarkParams.sorting,
        [chartType]: sortConfig,
      },
    };
  }

  reportSpecificHeaderAttrs(): Partial<ChartHeaderAttrs> {
    // TODO calculate attrs directly from bookmark params
    // for now, use the Report model for parity with Insights app
    const report = Report.fromBookmarkData(this.bookmarkParams);
    const timeComparisonDisabledReason = getTimeComparisonDisabledReason(report); // TODO overrides
    const breakdowns = getSelectedSegments(report.sections.cohorts.clauses, report.sections.group.clauses, false);
    const enableLiftComparison = isLiftEnabled();

    // TODO hopefully some of these can be shared among different report types
    return {
      breakdowns,
      'chart-type-overrides': report.getChartTypeSectionOverrides(),
      'chart-types': ChartResultChartTypes,
      'comparison-date-range': report.timeComparison,
      'date-range': getDateRange(report.sections.time.clauses).getAttributes(),
      'date-unit-overrides': report.getTimeUnitSectionOverrides(),
      'date-units':
        report.showTimeUnitAggregation() && OrderedStandardTimeUnits.filter((value) => value !== TimeUnit.Year),
      'selected-chart-type': getSelectedChartType(report),
      'selected-date-unit': report.timeUnit(),
      'show-date-range-selector': !report.sections.show.isPeopleOnlyQuery(),
      'time-comparison-disabled-reason': getTimeComparisonDisabledReasonMessage(report, timeComparisonDisabledReason),

      // lift-specific attrs
      'enable-lift-comparison': enableLiftComparison,
      'selected-breakdown-segments': getLeafLevelSegments({
        breakdowns,
        queryResults: this.results,
        report,
      }),
      'selected-value-representation': report.getValueRepresentation(),
      'value-representations': getValueRepresentations(enableLiftComparison, report.displayOptions),
      'selected-lift-comparison': report.liftComparison,
      'lift-type-overrides': report.getLiftTypeOverrides(),
    };
  }

  protected headerChildrenTemplate() {
    // Analysis type menu (linear, rolling, etc)
    const report = Report.fromBookmarkData(this.bookmarkParams);
    const sections = getAnalysisMenuItems(report);

    const sortControls = this.dataTableChartEnabled() &&
      report.displayOptions.chartType === ChartType.Table &&
      (Boolean(this.attr(`status`)) === false || this.attr(`status`) === ReportStatus.Loading) && (
        <>
          <DataSortMenuEl
            attrs={{
              slot: `report-controls`,
            }}
            params={{
              isLoading: this.attr(`status`) === ReportStatus.Loading,
              sortConfig: this.getContext(`segmentFilterController`)?.tableSortConfig,
              defaultSortConfig: this.getContext(`segmentFilterController`)?.defaultSortConfig,
              columns: this.getContext(`segmentFilterController`)?.getSections()[0]?.tableColumns,
              chartType: this.bookmarkParams.displayOptions.chartType,

              onSortChange: (ev: SortChangeEvent) => {
                this.dispatchUpdate({
                  params: this.getBookmarkParamsFromSortChange(ev),
                  requery: false,
                });
              },
            }}
          />
        </>
      );

    const analysisControls = sections && (
      <>
        {report.getDisplayAnalysis() === AnalysisType.Rolling && (
          <div sel='.chart-control-extra'>
            <SelectEl
              attrs={{
                'selected-option': getSelectedRollingOption(report),
                sections: getRollingOptions(report),
              }}
              on={{
                change: (ev) => this.handleRollingWindowChange(ev.detail.item.value),
              }}
            />
          </div>
        )}
        <div sel='.chart-control'>
          <SelectEl
            attrs={{
              'selected-option': getSelectedAnalysis(report),
              icon: {leftIcon: getSelectedAnalysisIcon(report)},
              'menu-width': 160,
              sections,
            }}
            on={{
              change: (ev) => this.handleAnalysisSelectChange(ev.detail.item.value),
            }}
          />
        </div>
      </>
    );

    if (analysisControls || sortControls) {
      return (
        <>
          {sortControls}
          <div sel='.chart-controls-extra' attrs={{slot: `chart-controls`}}>
            {analysisControls}
          </div>
        </>
      );
    } else {
      return null;
    }
  }

  updateDisplayOptions(options: DisplayOptions) {
    const oldDisplayOptions = this.bookmarkParams.displayOptions;
    const newDisplayOptions = correctDisplayOptions(options);

    if (!isEqual(oldDisplayOptions, newDisplayOptions)) {
      const requery = shouldRequeryDisplayOptions(oldDisplayOptions, newDisplayOptions);
      this.dispatchUpdate({
        params: {...this.bookmarkParams, displayOptions: newDisplayOptions},
        requery,
      });
    }
  }

  handleRollingWindowChange(rollingWindowSize) {
    this.updateDisplayOptions({
      ...this.bookmarkParams.displayOptions,
      rollingWindowSize,
    });
  }

  handleAnalysisSelectChange(analysis: AnalysisType) {
    const {displayOptions} = bookmarkUpdatesFromAnalysisChange(this.bookmarkParams, analysis);
    this.updateDisplayOptions(displayOptions);
  }

  handleDataTableColumnResize(ev: ColumnResizeEvent) {
    this.update({tableChartColumnWidthOverrides: ev.detail.columnWidthOverrides});
  }

  bookmarkUpdateFromHeaderUpdate(update: ChartHeaderUpdateDetail) {
    const report = Report.fromBookmarkData(this.bookmarkParams);
    const enableLiftComparison = isLiftEnabled();
    const bookmarkUpdates = bookmarkUpdatesFromHeaderUpdate(
      report,
      update,
      enableLiftComparison,
      this.state.hasChangedTimeUnit,
    );

    if (getOwnAttr(update.delta, `dateUnit`)) {
      this.update({hasChangedTimeUnit: true});
    }

    let requery = true;
    let hasChangedChartType = false;
    if (bookmarkUpdates.displayOptions) {
      requery = shouldRequeryDisplayOptions(this.bookmarkParams.displayOptions, bookmarkUpdates.displayOptions);
      const {chartType, plotStyle} = this.bookmarkParams.displayOptions;
      hasChangedChartType =
        chartType !== bookmarkUpdates.displayOptions.chartType ||
        plotStyle !== bookmarkUpdates.displayOptions.plotStyle;
    }

    const params = {...this.bookmarkParams, ...bookmarkUpdates};
    return {params, queryReason: QueryReason.QbOtherUpdate, requery, hasChangedChartType};
  }
}

export const InsightsVisualizationEl = defineAndMakeJsxFactory(`insights-visualization`, InsightsVisualization);
export const InsightsVisualizationReact = panel2react(`insights-visualization`, InsightsVisualization);
